import { PromotionGroupType, SelectionPromotion } from '@teko/cart/dist/v2';
import { useProductDetailState, useTenantConfig } from 'contexts';
import { DefaultPromotions } from 'modules/product-detail/shared/DefaultPromotions';
import { TierPromotions } from 'modules/product-detail/shared/TierPromotion';
import React, { useMemo } from 'react';
import { SelectionPromotions } from './SelectionPromotions';

const MAX_LINES_BEFORE_ELLIPSIS = 2;

const isPromotionDefaultGroupType = (
  promotions: SelectionPromotion[],
  groupType?: PromotionGroupType,
): boolean => {
  return groupType
    ? groupType === PromotionGroupType.GROUP_TYPE_DEFAULT
    : promotions.every((p) => !p.isSelectable);
};

const mapTierPromotions = (promotions: SelectionPromotion[]) =>
  promotions.map((promotion) => ({
    cartPromotionId: null,
    promotionId: promotion.promotionId,
    promotionCode: promotion.promotionCode,
    isSelectable: promotion.isSelectable,
    imageUrl: promotion.imageUrl,
    title: promotion.title,
    description: promotion.description,
    endedAt: promotion.endedAt?.toString() || '',
    name: promotion.name || '',
    tierDescriptions: promotion.tierDescriptions,
    highlightedDescriptions: promotion.highlightedDescriptions,
    customerTierDescriptions: promotion.customerTierDescriptions,
  }));

const buildTierPromotionProps = (promotions: SelectionPromotion[]) => [
  {
    title: '',
    groupType: undefined,
    promotions: mapTierPromotions(promotions),
  },
];

const hasVisibleDefaultPromotions = (promotions: SelectionPromotion[]) =>
  promotions.some((promotion) => !!promotion.title);

const buildNewPromotionBlocks = (
  promotionBlocks: {
    title: string;
    groupType?: PromotionGroupType;
    promotions: SelectionPromotion[];
    description?: string;
  }[],
) => {
  if (!promotionBlocks.length) return [];

  const tierPromotionBlock = {
    title: '',
    groupType: PromotionGroupType.GROUP_TYPE_TIER_PROMOTIONS,
    promotions: promotionBlocks.flatMap((p) => p.promotions),
    description: '',
  };

  return [...promotionBlocks, tierPromotionBlock];
};

const renderPromotionBlock = (
  block: {
    promotions: SelectionPromotion[];
    groupType?: PromotionGroupType;
    title: string;
    description?: string;
  },
  index: number,
  showCustomerTierPromotions?: boolean,
  showTierPromotions?: boolean,
): React.ReactNode => {
  const isDefaultGroup = isPromotionDefaultGroupType(
    block.promotions,
    block.groupType,
  );

  if (isDefaultGroup) {
    if (!hasVisibleDefaultPromotions(block.promotions)) return null;
    // TODO: New UI for defaultPromotions update here later
    return <DefaultPromotions promotions={block.promotions} key={index} />;
  }

  // TODO: Check and return promotion type Selectable & Exclusive here

  // Tier promotions
  if (block.groupType === PromotionGroupType.GROUP_TYPE_TIER_PROMOTIONS) {
    const tierProps = buildTierPromotionProps(block.promotions);
    return (
      <React.Fragment key={index}>
        {showCustomerTierPromotions && (
          <TierPromotions
            productPromotions={tierProps}
            isCustomerTierPromotions
          />
        )}
        {showTierPromotions && (
          <TierPromotions
            productPromotions={tierProps}
            maxLinesBeforeEllipsis={MAX_LINES_BEFORE_ELLIPSIS}
          />
        )}
      </React.Fragment>
    );
  }

  return (
    <SelectionPromotions
      title={block?.title}
      description={block?.description}
      key={index}
      promotions={block.promotions}
    />
  );
};

export const ProductPromotions: React.FC = () => {
  const { promotionBlocks } = useProductDetailState();
  const { epicOmni1428, epicOmni1585 } = useTenantConfig();

  const showTierPromotionsBlock = !!epicOmni1428 || !!epicOmni1585;

  const blocks = useMemo(
    () =>
      showTierPromotionsBlock
        ? buildNewPromotionBlocks(promotionBlocks)
        : promotionBlocks,
    [promotionBlocks, showTierPromotionsBlock],
  );

  return (
    <>
      {blocks.map((block, index) =>
        renderPromotionBlock(block, index, epicOmni1585, epicOmni1428),
      )}
    </>
  );
};
